import styled, { css } from 'styled-components';
import { Dialog, Modal, ModalOverlay } from 'react-aria-components';
import { Link } from '@/components/Link';
import type { HighlightColour } from '@/types';

// #2947 Resolves a colourway to the custom property header.css defines at :root. The hex values
// stay in the stylesheet so the two renderers cannot drift apart.
const highlightColour = (colour?: HighlightColour) =>
  colour === 'black-friday'
    ? 'var(--menu-highlight-black-friday)'
    : 'var(--menu-highlight-christmas-red)';

const sharedLinkContainerStyles = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-large);
  padding: var(--spacing-base);
  width: 100%;
`;

export const FeaturedLinks = styled.div<{ $highlightColour?: HighlightColour }>`
  ${sharedLinkContainerStyles}
  background: var(--brand-200);
  color: var(--brand-900);

  /* #2947 Mobile half of the highlighted mega menu's featured column. The links need an
     explicit override rather than inheriting: sharedLinkStyles sets colour directly on the
     anchor, so an inherited container colour never reaches them. The focus chip is recoloured
     for the same reason the desktop one is - the shared lime chip reads green on pink. */
  ${props =>
    props.$highlightColour
      ? `
  background: var(--menu-highlight-featured-surface);
  color: var(--black);

  & a,
  & a[data-hovered] {
    color: var(--black);
    text-decoration-color: var(--black);
  }

  & a[data-focus-visible] {
    background: var(--white);
    box-shadow:
      0 0 0 4px ${highlightColour(props.$highlightColour)},
      0 1px 2px 0 rgb(10 13 18 / 0.05);
    text-decoration-color: var(--black);
  }
    `
      : ''}

  & ul {
    ${sharedLinkContainerStyles}
    padding: 0;
    list-style: none;

    & li {
      width: 100%;
    }
  }
`;

export const LinksWrapper = styled.nav`
  ${sharedLinkContainerStyles}
  background: var(--white);

  & ul {
    ${sharedLinkContainerStyles}
    padding: 0;
    list-style: none;

    & li {
      width: 100%;
    }
  }
`;

export const Menu = styled(Dialog)`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 0;
  height: 100vh;
  background: var(--white);
  overflow-y: auto;

  &:focus-visible {
    outline: none;
  }

  & .react-aria-MenuItem {
    width: fit-content;

    &[data-focus-visible],
    &[data-focused] {
      background: var(--brand-100);
      border-radius: 4px;
      box-shadow: var(--shadow-focus);
      text-decoration: underline;
      text-decoration-color: var(--brand-900);
      text-underline-offset: 3px;
      outline: 0;
    }
  }
`;

export const MenuHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: var(--spacing-base);
  background: var(--white);
  border-block-end: 1px solid var(--grey-100);
  position: sticky;
  top: 0;
`;

export const Overlay = styled(Modal)`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  position: absolute;
  top: 0;
  left: 0;

  &[data-entering] {
    animation: mymodal-slide 300ms;
  }

  &[data-exiting] {
    animation: mymodal-slide 300ms reverse ease-in;
  }

  @keyframes mymodal-slide {
    from {
      transform: translateX(-100%);
    }

    to {
      transform: translateX(0);
    }
  }
`;

export const ProductLinks = styled(LinksWrapper)`
  flex: 1;
  background: var(--white);
`;

// #2947 Plain top-level trunk (no submenu) carrying the landing page's "Highlight Menu" tick.
//
// Hover and focus both need explicit overrides rather than just a resting colour:
// sharedLinkStyles sets [data-hovered] to --grey-600 and [data-focus-visible] to the lime
// --brand-100 chip, and both out-specify a bare `color:` declaration. Left alone, hovering a
// highlighted item drops it to grey (desktop does the opposite and intensifies it) and the
// focus chip reads lime on red - the same contrast problem already fixed on desktop and in
// FeaturedLinks below.
export const NavLink = styled(Link)<{ $highlightColour?: HighlightColour }>`
  ${props =>
    props.$highlightColour
      ? `
  color: ${highlightColour(props.$highlightColour)};
  text-decoration-color: ${highlightColour(props.$highlightColour)};

  &[data-hovered] {
    color: ${highlightColour(props.$highlightColour)};
    text-decoration-color: ${highlightColour(props.$highlightColour)};
  }

  &[data-focus-visible] {
    background: var(--white);
    box-shadow:
      0 0 0 4px ${highlightColour(props.$highlightColour)},
      0 1px 2px 0 rgb(10 13 18 / 0.05);
    text-decoration-color: ${highlightColour(props.$highlightColour)};
  }
    `
      : ''}
`;

export const SubMenuToggle = styled(Link)<{
  $isBackButton?: boolean;
  $highlightColour?: HighlightColour;
}>`
  display: flex;
  justify-content: ${props => (props.$isBackButton ? 'flex-start' : 'space-between')};
  align-items: center;
  width: ${props => (props.$isBackButton ? 'fit-content' : '100%')};
  text-align: left;
  ${props =>
    props.$isBackButton
      ? `
  color: var(--grey-500);
    `
      : ''}
  ${props =>
    props.$highlightColour
      ? `color: ${highlightColour(props.$highlightColour)};`
      : ''}

  & svg {
    width: 20px;

    & path {
      stroke: var(--grey-500);
    }
  }

  /* #2947 Sits after the generic "& svg" rule above so the chevron recolour wins on source
     order, matching the desktop partial where a highlighted trunk tints its chevron too. The
     hover and focus overrides are here for the same reason as NavLink. */
  ${props =>
    props.$highlightColour
      ? `
  &[data-hovered] {
    color: ${highlightColour(props.$highlightColour)};
  }

  &[data-focus-visible] {
    background: var(--white);
    box-shadow:
      0 0 0 4px ${highlightColour(props.$highlightColour)},
      0 1px 2px 0 rgb(10 13 18 / 0.05);
  }

  & svg path {
    stroke: ${highlightColour(props.$highlightColour)};
  }
    `
      : ''}
`;

export const HeadingLink = styled.a`
  text-decoration: none;
  color: inherit;
`;

export const Underlay = styled(ModalOverlay)`
  position: fixed;
  inset: 0;
  background: rgb(0 0 0 / 0.5);
  backdrop-filter: blur(12px);
  z-index: 9999;

  &[data-entering] {
    animation: mymodal-blur 300ms;
  }

  &[data-exiting] {
    animation: mymodal-blur 300ms reverse ease-in;
  }

  @keyframes mymodal-blur {
    from {
      background: rgba(0 0 0 / 0);
      backdrop-filter: blur(0);
    }

    to {
      background: rgba(0 0 0 / 0.5);
      backdrop-filter: blur(12px);
    }
  }
`;

export const UtilityLinks = styled(LinksWrapper)`
  background: var(--grey-100);
  padding-bottom: 100px;
`;
